import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  LogOut, Loader2, Check, X, Ban, Trash2, ArrowLeft, RefreshCw, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import ServicesTab from "@/components/admin/ServicesTab";
import AvailabilityTab from "@/components/admin/AvailabilityTab";
import PushToggle from "@/components/admin/PushToggle";
import UsersTab from "@/components/admin/UsersTab";
import BookingDetail from "@/components/admin/BookingDetail";

const EASE = [0.22, 1, 0.36, 1];
const SLOTS = Array.from({ length: 13 }, (_, i) => `${String(i + 8).padStart(2, "0")}:00`);
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PRESETS = [
  {
    id: "school-hours",
    label: "School hours",
    hint: "Mon–Fri · 8 AM–3 PM",
    weekdays: [1, 2, 3, 4, 5],
    time_slots: ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00"],
    reason: "School hours",
  },
  {
    id: "sunday-mornings",
    label: "Sunday mornings",
    hint: "Sun · 8 AM–12 PM",
    weekdays: [0],
    time_slots: ["08:00", "09:00", "10:00", "11:00"],
    reason: "Sunday mornings",
  },
  {
    id: "weekday-evenings",
    label: "Weekday evenings",
    hint: "Mon–Fri · 3 PM–4 PM",
    weekdays: [1, 2, 3, 4, 5],
    time_slots: ["15:00", "16:00"],
    reason: "Weekday evenings",
  },
];

const slotLabel = (s) => {
  const h = parseInt(s, 10);
  if (h === 12) return "12 PM";
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-limed-light text-pine",
  declined: "bg-slate-100 text-slate-500",
};

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("asc_token", data.access_token);
      localStorage.setItem("asc_user", JSON.stringify(data.user));
      onLogin(data.access_token, data.user);
      toast.success(`Welcome back, ${data.user.name}!`);
    } catch (err) {
      toast.error(formatApiError(err, "Login failed. Check your email and password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage/40 flex items-center justify-center px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        data-testid="admin-login-modal"
        className="w-full max-w-sm bg-white rounded-3xl border border-emerald-950/10 shadow-xl shadow-emerald-950/5 p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <Logo size={38} />
          <div>
            <p className="font-display font-bold text-emerald-950 leading-none">Crew Login</p>
            <p className="text-xs text-emerald-900/50 mt-1">All-Season Crew admin</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3.5">
          <input
            data-testid="admin-login-email"
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-emerald-950/15 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
          />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-emerald-950/15 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
          />
          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-pine text-bone font-semibold px-6 py-3 rounded-full hover:bg-limed hover:text-pine-dark transition-colors duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
        <Link
          to="/"
          className="mt-5 inline-flex items-center gap-1.5 text-xs text-emerald-900/50 hover:text-pine transition-colors"
        >
          <ArrowLeft size={12} /> Back to site
        </Link>
      </motion.div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("asc_token"));
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("asc_user") || "null");
    } catch {
      return null;
    }
  });
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockForm, setBlockForm] = useState({ date: "", time_slot: "08:00", reason: "" });
  const [recForm, setRecForm] = useState({ weekdays: [], time_slots: [], reason: "" });
  const [recSaving, setRecSaving] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, bl, rec] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/blocked"),
        api.get("/admin/recurring-blocks"),
      ]);
      setBookings(b.data);
      setBlocked(bl.data);
      setRecurring(rec.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("asc_token");
        localStorage.removeItem("asc_user");
        setToken(null);
        setUser(null);
      } else {
        toast.error("Couldn't load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const logout = () => {
    localStorage.removeItem("asc_token");
    localStorage.removeItem("asc_user");
    setToken(null);
    setUser(null);
    setTab("bookings");
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking ${status}.`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteBooking = async (id, name) => {
    if (!window.confirm(`Delete the booking for ${name}? This frees up the slot and can't be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted — the slot is open again.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const blockSlot = async (e) => {
    e.preventDefault();
    if (!blockForm.date) return toast.error("Pick a date to block.");
    try {
      await api.post("/admin/blocked", blockForm);
      toast.success("Slot blocked.");
      setBlockForm({ date: "", time_slot: "08:00", reason: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unblock = async (id) => {
    try {
      await api.delete(`/admin/blocked/${id}`);
      toast.success("Slot unblocked.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleListValue = (list, value) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  const saveRecurring = async (payload) => {
    if (!payload.weekdays.length || !payload.time_slots.length) {
      return toast.error("Pick at least one day and one time slot.");
    }
    setRecSaving(true);
    try {
      await api.post("/admin/recurring-blocks", payload);
      toast.success("Recurring block added — those slots are now off-limits every week.");
      setRecForm({ weekdays: [], time_slots: [], reason: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecSaving(false);
    }
  };

  const deleteRecurring = async (id) => {
    try {
      await api.delete(`/admin/recurring-blocks/${id}`);
      toast.success("Recurring block removed.");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!token) {
    return (
      <LoginCard
        onLogin={(t, u) => {
          setToken(t);
          setUser(u);
          setTab("bookings");
        }}
      />
    );
  }

  const isAdmin = user?.role === "admin";
  const TABS = [
    { id: "bookings", label: `Bookings (${bookings.length})` },
    ...(isAdmin ? [{ id: "services", label: "Services & Pricing" }, { id: "availability", label: "Availability" }] : []),
    { id: "blocked", label: `Blocked Slots (${blocked.length})` },
    ...(isAdmin ? [{ id: "users", label: "Users" }] : []),
  ];

  return (
    <div className="min-h-screen bg-sage/40">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-emerald-950/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <div>
              <span className="font-display font-bold text-emerald-950">Crew Dashboard</span>
              {user?.name && (
                <span data-testid="admin-user-label" className="block text-[11px] font-mono uppercase tracking-widest text-emerald-900/40 leading-tight">
                  {user.name} · {user.role}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PushToggle />
            <button
              data-testid="admin-refresh-btn"
              onClick={loadAll}
              className="p-2 rounded-full text-emerald-900/50 hover:text-pine hover:bg-sage transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <Link
              data-testid="admin-home-mobile-btn"
              to="/"
              title="Back to website"
              className="sm:hidden p-2 rounded-full text-emerald-900/60 hover:text-pine hover:bg-sage transition-colors"
            >
              <ArrowLeft size={16} />
            </Link>
            <Link
              to="/"
              className="hidden sm:inline-block text-sm font-medium text-emerald-900/60 hover:text-pine transition-colors px-3 py-2"
            >
              View site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-pine hover:text-limed transition-colors px-2 sm:px-3 py-2"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div data-testid="admin-dashboard-tabs" className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                tab === t.id
                  ? "bg-pine text-bone"
                  : "bg-white text-emerald-900/60 border border-emerald-950/10 hover:border-limed"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-pine">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : tab === "bookings" ? (
          <div data-testid="admin-bookings-table" className="space-y-3">
            {bookings.length === 0 && (
              <p className="text-center text-emerald-900/50 py-16 bg-white rounded-3xl border border-emerald-950/10">
                No booking requests yet.
              </p>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid={`admin-booking-row-${b.id}`}
                onClick={() => setDetailId(b.id)}
                title="Click to view details, notes & photos"
                className="bg-white rounded-2xl border border-emerald-950/10 p-5 flex flex-wrap items-center gap-4 cursor-pointer hover:border-limed/60 hover:shadow-lg hover:shadow-emerald-950/5 transition-all duration-200"
              >
                <div className="min-w-[150px]">
                  <p className="font-display font-bold text-emerald-950">
                    {b.date} · {slotLabel(b.time_slot)}
                  </p>
                  <p className="text-sm text-emerald-900/60">{b.service}</p>
                </div>
                <div className="flex-1 min-w-[180px] text-sm text-emerald-900/70">
                  <p className="font-semibold text-emerald-950">{b.name}</p>
                  <p>{b.phone} · {b.email}</p>
                  <p>{b.address}</p>
                  {b.notes && <p className="italic text-emerald-900/50 mt-1">"{b.notes}"</p>}
                </div>
                <span
                  className={`text-xs font-mono font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full ${STATUS_STYLES[b.status]}`}
                >
                  {b.status}
                </span>
                {b.status === "confirmed" && (
                  <span
                    data-testid={`admin-reminder-status-${b.id}`}
                    className={`text-[11px] font-mono font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full ${
                      b.reminder_sent ? "bg-sky-100 text-sky-800" : "bg-sage text-emerald-900/40"
                    }`}
                  >
                    {b.reminder_sent
                      ? `Reminder sent${b.reminder_sent_at ? ` · ${new Date(b.reminder_sent_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}`
                      : "Reminder pending"}
                  </span>
                )}
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`admin-confirm-btn-${b.id}`}
                    onClick={() => setStatus(b.id, "confirmed")}
                    disabled={b.status === "confirmed"}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-pine text-bone px-4 py-2 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-40"
                  >
                    <Check size={13} /> Confirm
                  </button>
                  <button
                    data-testid={`admin-decline-btn-${b.id}`}
                    onClick={() => setStatus(b.id, "declined")}
                    disabled={b.status === "declined"}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors disabled:opacity-40"
                  >
                    <X size={13} /> Decline
                  </button>
                  <button
                    data-testid={`admin-delete-booking-btn-${b.id}`}
                    onClick={() => deleteBooking(b.id, b.name)}
                    title="Delete booking"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-3 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : tab === "services" ? (
          <ServicesTab />
        ) : tab === "availability" ? (
          <AvailabilityTab />
        ) : tab === "users" ? (
          <UsersTab currentEmail={user?.email} />
        ) : (
          <div>
            <form
              onSubmit={blockSlot}
              className="bg-white rounded-3xl border border-emerald-950/10 p-6 flex flex-wrap items-end gap-4 mb-6"
            >
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">
                  Date
                </label>
                <input
                  data-testid="admin-block-date"
                  type="date"
                  required
                  value={blockForm.date}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                  className="rounded-xl border border-emerald-950/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">
                  Time
                </label>
                <select
                  data-testid="admin-block-time"
                  value={blockForm.time_slot}
                  onChange={(e) => setBlockForm({ ...blockForm, time_slot: e.target.value })}
                  className="rounded-xl border border-emerald-950/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50 bg-white"
                >
                  {SLOTS.map((s) => (
                    <option key={s} value={s}>
                      {slotLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-1.5 block">
                  Reason (optional)
                </label>
                <input
                  data-testid="admin-block-reason"
                  placeholder="e.g. Family event"
                  value={blockForm.reason}
                  onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                  className="w-full rounded-xl border border-emerald-950/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                />
              </div>
              <button
                data-testid="admin-block-slot-btn"
                type="submit"
                className="inline-flex items-center gap-2 bg-pine text-bone font-semibold px-6 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors"
              >
                <Ban size={15} /> Block Slot
              </button>
            </form>

            <div className="bg-white rounded-3xl border border-emerald-950/10 p-6 mb-6">
              <div className="flex items-center gap-2.5 mb-1.5">
                <Repeat size={17} className="text-limed" />
                <h3 className="font-display font-bold text-emerald-950">Recurring blocks</h3>
              </div>
              <p className="text-sm text-emerald-900/60 mb-5">
                Block standing times once — they grey out on the calendar every week.
              </p>

              <div className="flex flex-wrap gap-2.5 mb-6">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`admin-preset-${p.id}`}
                    onClick={() =>
                      saveRecurring({ weekdays: p.weekdays, time_slots: p.time_slots, reason: p.reason })
                    }
                    disabled={recSaving}
                    className="text-left bg-sage/60 border border-emerald-950/10 rounded-2xl px-4 py-3 hover:border-limed hover:bg-limed-light/60 transition-colors duration-200 disabled:opacity-50"
                  >
                    <p className="text-sm font-semibold text-pine">{p.label}</p>
                    <p className="text-[11px] font-mono uppercase tracking-widest text-emerald-900/50 mt-0.5">
                      {p.hint}
                    </p>
                  </button>
                ))}
              </div>

              <div className="border-t border-emerald-950/10 pt-5">
                <p className="text-xs font-mono uppercase tracking-widest text-emerald-900/50 mb-2">
                  Or build your own
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {WEEKDAYS.map((d, i) => (
                    <button
                      key={d}
                      data-testid={`admin-rec-day-${d.toLowerCase()}`}
                      onClick={() =>
                        setRecForm({ ...recForm, weekdays: toggleListValue(recForm.weekdays, i) })
                      }
                      className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${
                        recForm.weekdays.includes(i)
                          ? "bg-pine text-bone"
                          : "bg-sage/60 text-emerald-900/60 hover:bg-sage"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {SLOTS.map((s) => (
                    <button
                      key={s}
                      data-testid={`admin-rec-time-${s}`}
                      onClick={() =>
                        setRecForm({ ...recForm, time_slots: toggleListValue(recForm.time_slots, s) })
                      }
                      className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors duration-200 ${
                        recForm.time_slots.includes(s)
                          ? "bg-limed text-pine-dark"
                          : "bg-sage/60 text-emerald-900/60 hover:bg-sage"
                      }`}
                    >
                      {slotLabel(s)}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    data-testid="admin-rec-reason"
                    placeholder="Reason (optional) — e.g. Football practice"
                    value={recForm.reason}
                    onChange={(e) => setRecForm({ ...recForm, reason: e.target.value })}
                    className="flex-1 min-w-[200px] rounded-xl border border-emerald-950/15 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50"
                  />
                  <button
                    data-testid="admin-rec-save-btn"
                    onClick={() => saveRecurring(recForm)}
                    disabled={recSaving}
                    className="inline-flex items-center gap-2 bg-pine text-bone font-semibold px-6 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-60"
                  >
                    {recSaving ? <Loader2 size={15} className="animate-spin" /> : <Repeat size={15} />}
                    Block Every Week
                  </button>
                </div>
              </div>

              {recurring.length > 0 && (
                <div className="mt-5 space-y-2.5">
                  {recurring.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`admin-recurring-row-${r.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 bg-sage/40 rounded-2xl px-4 py-3"
                    >
                      <p className="text-sm text-emerald-950">
                        <span className="font-semibold">
                          Every {r.weekdays.map((w) => WEEKDAYS[w]).join(", ")}
                        </span>
                        <span className="text-emerald-900/60">
                          {" "}
                          · {r.time_slots.map(slotLabel).join(", ")}
                        </span>
                        {r.reason && (
                          <span className="text-emerald-900/50"> — {r.reason}</span>
                        )}
                      </p>
                      <button
                        data-testid={`admin-recurring-delete-${r.id}`}
                        onClick={() => deleteRecurring(r.id)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3">
              {blocked.length === 0 && (
                <p className="text-center text-emerald-900/50 py-16 bg-white rounded-3xl border border-emerald-950/10">
                  No blocked slots.
                </p>
              )}
              {blocked.map((b) => (
                <div
                  key={b.id}
                  data-testid={`admin-blocked-row-${b.id}`}
                  className="bg-white rounded-2xl border border-emerald-950/10 p-4 flex items-center justify-between gap-4"
                >
                  <p className="text-sm font-semibold text-emerald-950">
                    {b.date} · {slotLabel(b.time_slot)}
                    {b.reason && <span className="font-normal text-emerald-900/50"> — {b.reason}</span>}
                  </p>
                  <button
                    data-testid={`admin-unblock-btn-${b.id}`}
                    onClick={() => unblock(b.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={13} /> Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <BookingDetail
          bookingId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={loadAll}
        />
      </main>
    </div>
  );
}
